import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LoanApplicationForm from '@/components/LoanApplicationForm';
import { 
  Zap, 
  Clock, 
  ShieldCheck, 
  FileText, 
  Users, 
  TrendingUp,
  CheckCircle2,
  Building2,
  Wallet,
  ArrowRight,
  Star,
  BarChart3,
  Lock,
  Headphones,
  GitCompareArrows
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const benefits = [
  {
    icon: Clock,
    title: 'Lightning Fast',
    description: 'Eligibility decision in as little as 4 hours. Funds paid within 24 hours.'
  },
  {
    icon: ShieldCheck,
    title: 'Wide Acceptance',
    description: 'Start-ups & established businesses accepted. Wide range of credit profiles considered.'
  },
  {
    icon: FileText,
    title: 'Minimal Paperwork',
    description: 'Simple application process with minimal documentation required.'
  },
  {
    icon: Users,
    title: 'Dedicated Support',
    description: 'Personal account manager throughout. UK-based team available 6 days a week.'
  },
  {
    icon: Wallet,
    title: 'Flexible Options',
    description: 'Unsecured & secured funding options. Flexible loan terms tailored to your needs.'
  },
  {
    icon: TrendingUp,
    title: 'High Approval Rates',
    description: 'Higher approval rates compared to traditional lenders. Early settlement discounts available.'
  }
];

const stats = [
  { value: '£500M+', label: 'Funded to UK Businesses' },
  { value: '4,500+', label: 'Businesses Supported' },
  { value: '4 Hours', label: 'Average Decision Time' },
  { value: '98%', label: 'Customer Satisfaction' }
];

const testimonials = [
  {
    quote: "QuickFinance helped us secure £250,000 for expansion. The process was incredibly smooth and we had the funds within 24 hours.",
    author: "Sarah Mitchell",
    role: "CEO, TechStart Ltd",
    rating: 5
  },
  {
    quote: "As a startup, we struggled with traditional lenders. QuickFinance understood our potential and provided the working capital we needed.",
    author: "James Chen",
    role: "Founder, GreenLogistics",
    rating: 5
  },
  {
    quote: "Professional, fast, and transparent. Our dedicated account manager made the entire process stress-free.",
    author: "Emma Thompson",
    role: "Director, BuildRight Construction",
    rating: 5
  }
];

export default function LandingPage() {
  const navigate = useNavigate();

  const scrollToApply = () => {
    const element = document.getElementById('apply');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Content */}
            <div className="animate-fade-in">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-emerald-200">
                <ShieldCheck size={16} />
                Trusted by 4,500+ UK Businesses
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-950 tracking-tight leading-tight mb-6 font-['Outfit']">
                Business Funding
                <span className="block text-blue-700">Made Simple</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 max-w-lg">
                Access £10,000 to £10 million in business funding with fast decisions and flexible terms. Built for ambitious UK businesses.
              </p>

              {/* Key benefits list */}
              <ul className="space-y-3 mb-8">
                {[
                  'Decision in as little as 4 hours',
                  'Funds paid out within 24 hours',
                  'Fixed, predictable monthly repayments',
                  'No hidden fees or early repayment penalties'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-slate-200">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']">{stat.value}</div>
                    <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div id="apply" className="animate-slide-up stagger-2">
              <LoanApplicationForm />
            </div>
          </div>
        </div>
      </section>

      {/* Lender Network */}
      <section className="bg-white border-y border-slate-200 py-10" data-testid="lender-network">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold tracking-wider uppercase text-slate-400 mb-6">Our Lender Panel</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {[
              'Funding Circle', 'Iwoca', 'MarketFinance', 'Liberis', 'Capify', '365 Business Finance',
              'Nucleus', 'Fleximize', 'Simply Asset Finance', 'Close Brothers', 'Metro Bank', 'Tide'
            ].map((name) => (
              <div key={name} className="flex items-center justify-center h-12 px-3 rounded-lg border border-slate-100 bg-slate-50/60">
                <span className="text-xs sm:text-sm font-semibold text-slate-500 whitespace-nowrap truncate">{name}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-slate-100">
            <span className="text-xs font-medium text-slate-400 px-3 py-1 rounded-md bg-slate-50 border border-slate-100">FCA Registered</span>
            <span className="text-xs font-medium text-slate-400 px-3 py-1 rounded-md bg-slate-50 border border-slate-100">SSL Secured</span>
            <span className="text-xs font-medium text-slate-400 px-3 py-1 rounded-md bg-slate-50 border border-slate-100">GDPR Compliant</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 sm:py-32 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight font-['Outfit']">
              Business Funding That Works For You
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              We've streamlined the funding process to get your business the capital it needs, when it needs it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <div 
                key={i} 
                className="bg-white border border-slate-200 rounded-2xl p-6 card-hover"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Outfit']">{benefit.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Funding Purposes Section */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="funding-purposes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">Flexible Funding</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6 font-['Outfit']">
                Funding For Multiple Purposes
              </h2>
              <p className="text-slate-600 mb-8">
                Whether you're looking to expand, manage cash flow, or invest in equipment, we have the right funding solution for your business needs.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Building2, text: 'Business Expansion' },
                  { icon: Wallet, text: 'Working Capital' },
                  { icon: Zap, text: 'Equipment Purchase' },
                  { icon: TrendingUp, text: 'VAT & Tax Payments' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200">
                    <item.icon className="w-5 h-5 text-blue-700" />
                    <span className="text-sm font-medium text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={scrollToApply}
                className="mt-8 bg-blue-700 hover:bg-blue-800 text-white btn-primary" 
                data-testid="cta-apply-btn"
              >
                Apply Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: BarChart3, title: 'Multi-Lender Access', desc: 'Compare offers from 30+ lenders in one place', color: 'bg-blue-600' },
                { icon: Clock, title: 'Fast Decisions', desc: 'Eligibility within 4 hours, funds in 24', color: 'bg-emerald-600' },
                { icon: Headphones, title: 'Dedicated Advisor', desc: 'A personal account manager from start to finish', color: 'bg-violet-600' },
                { icon: Lock, title: 'Secure Portal', desc: 'Track progress, upload docs & e-sign online', color: 'bg-amber-600' }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-3 card-hover">
                  <div className={`w-10 h-10 ${item.color} rounded-xl flex items-center justify-center`}>
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900 font-['Outfit']">{item.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 sm:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-4 block">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight font-['Outfit']">
              Trusted By Businesses Like Yours
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <div 
                key={i} 
                className="bg-white border border-slate-200 rounded-2xl p-6 card-hover"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.author}</div>
                  <div className="text-sm text-slate-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6 font-['Outfit']">
            Ready to Grow Your Business?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of UK businesses who have secured their funding through QuickFinance. Apply today and get your decision in as little as 4 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={scrollToApply}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8" 
              data-testid="cta-final-apply"
            >
              Apply Now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => navigate('/contact')}
              className="border-slate-600 text-white hover:bg-slate-800" 
              data-testid="cta-contact"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
